import type { Ref } from 'vue';

import { storeToRefs } from 'pinia';
import { computed, onScopeDispose, ref, watch } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi.ts';
import type { Availability, AvailabilityCalendarResponse, AvailabilityResponse } from '@/types/booking-api';
import type { BookingOption, ProductDTO } from '@/types/products';

import { DEBOUNCE_DELAY_MS, INITIAL_CALENDAR_MONTHS } from '@/composables/booking/booking-widget.constants';
import useResponsive from '@/composables/useResponsive';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { formatDateToLocalDate } from '@/utils/date';

export type BookingType = 'purchase' | 'gift';

export function useBookingWidget(cartsTransactionsApi: CartsTransactionsApi, apiKey: string, activityDetails: Ref<ProductDTO | undefined>) {
  cartsTransactionsApi.setApiKey(apiKey);
  const { currentLocale } = storeToRefs(useGlobalConfigurationStore());
  const { isMobile } = useResponsive();

  const bookingType = ref<BookingType>('purchase');
  const selectedOption = ref<BookingOption>();
  const selectedUnits = ref<Map<string, number>>(new Map());
  const selectedDate = ref<Date>();
  const selectedSession = ref<Availability | null>(null);

  const calendarData = ref<AvailabilityCalendarResponse>([]);
  const availableSessions = ref<AvailabilityResponse>([]);

  const isLoadingCalendar = ref(false);
  const isLoadingAvailabilities = ref(false);
  const isAddingToCart = ref(false);
  const bookingError = ref<string | null>(null);

  const hasCheckedOnce = ref(false);
  const dateDropdownOpener = ref<(() => void) | null>(null);
  const calendarPaginationResetter = ref<(() => void) | null>(null);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let calendarAbortController: AbortController | null = null;
  let availabilityAbortController: AbortController | null = null;

  const productTimezone = computed(() => activityDetails.value?.timeZone || 'UTC');

  watch(selectedOption, () => {
    selectedSession.value = null;
    hasCheckedOnce.value = false;
    selectedDate.value = undefined;
    availableSessions.value = [];
  });

  watch(selectedUnits, () => {
    selectedSession.value = null;

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      if (selectedOption.value && selectedUnits.value.size > 0) {
        resetCalendarPagination();
        if (hasCheckedOnce.value) {
          searchAvailabilities();
          loadCalendarAvailabilities();
        }
        else {
          selectedDate.value = undefined;
          loadCalendarAvailabilities();
        }
      }
    }, DEBOUNCE_DELAY_MS);
  }, { deep: true });

  async function loadCalendarAvailabilities(monthsFetched = 0, monthsToFetch = 1): Promise<boolean> {
    if (!selectedOption.value || selectedUnits.value.size === 0 || !activityDetails.value)
      return false;

    if (calendarAbortController) {
      calendarAbortController.abort();
    }
    calendarAbortController = new AbortController();

    isLoadingCalendar.value = true;
    bookingError.value = null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate: Date;

    let endDate: Date;

    if (monthsFetched > 0) {
      startDate = new Date(today);
      startDate.setMonth(today.getMonth() + monthsFetched, 1);

      endDate = new Date(today);
      endDate.setMonth(today.getMonth() + monthsFetched + monthsToFetch, 1);
    }
    else {
      startDate = today;
      endDate = new Date(today);
      endDate.setMonth(today.getMonth() + INITIAL_CALENDAR_MONTHS, 1);
      calendarData.value = [];
    }

    const request = {
      productId: activityDetails.value.id,
      optionId: selectedOption.value.id,
      localDateStart: `${formatDateToLocalDate(startDate)}T00:00:00Z`,
      localDateEnd: `${formatDateToLocalDate(endDate)}T00:00:00Z`,
      units: cartsTransactionsApi.buildUnitsFromSelection(selectedUnits.value),
    };

    try {
      const newCalendarData = await cartsTransactionsApi.fetchAvailabilityCalendar(
        request,
        currentLocale.value,
        calendarAbortController.signal,
      );

      const calendarMap = new Map(calendarData.value.map(day => [day.localDate, day]));
      newCalendarData.forEach((day) => {
        calendarMap.set(day.localDate, day);
      });

      calendarData.value = Array.from(calendarMap.values()).sort((a, b) => a.localDate.localeCompare(b.localDate));
      isLoadingCalendar.value = false;
      return true;
    }
    catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return false;
      }
      console.error('[useBookingWidget] Calendar error:', error);
      bookingError.value = 'errorCalendarLoad';
      isLoadingCalendar.value = false;
      return false;
    }
  }

  async function searchAvailabilities() {
    if (!selectedOption.value || selectedUnits.value.size === 0 || !selectedDate.value || !activityDetails.value)
      return;

    if (availabilityAbortController) {
      availabilityAbortController.abort();
    }
    availabilityAbortController = new AbortController();

    hasCheckedOnce.value = true;
    isLoadingAvailabilities.value = true;
    bookingError.value = null;

    const request = {
      productId: activityDetails.value.id,
      optionId: selectedOption.value.id,
      localDate: formatDateToLocalDate(selectedDate.value),
      units: cartsTransactionsApi.buildUnitsFromSelection(selectedUnits.value),
    };

    try {
      availableSessions.value = await cartsTransactionsApi.fetchAvailability(
        request,
        currentLocale.value,
        availabilityAbortController.signal,
      );

      const hasNoAvailableSessions = availableSessions.value.length === 0
        || availableSessions.value.every(session => !session.available);

      if (hasNoAvailableSessions) {
        selectedDate.value = undefined;
        resetCalendarPagination();
        loadCalendarAvailabilities();
      }
      isLoadingAvailabilities.value = false;
    }
    catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('[useBookingWidget] Availability error:', error);
      bookingError.value = 'errorAvailabilitySearch';
      isLoadingAvailabilities.value = false;
    }
  }

  function handleOptionSelected(option: BookingOption) {
    selectedOption.value = option;
  }

  function handleUnitsSelected(units: Map<string, number>) {
    selectedUnits.value = units;
  }

  function handleDateSelected(date: Date) {
    selectedDate.value = date;
  }

  function handleBookingTypeSelected(type: BookingType) {
    bookingType.value = type;
  }

  function handleSessionSelected(session: Availability) {
    selectedSession.value = session;
  }

  function registerDateDropdownOpener(opener: () => void) {
    dateDropdownOpener.value = opener;
  }

  function openDateDropdown() {
    dateDropdownOpener.value?.();
  }

  function registerCalendarPaginationResetter(resetter: () => void) {
    calendarPaginationResetter.value = resetter;
  }

  function resetCalendarPagination() {
    calendarPaginationResetter.value?.();
  }

  function resetAfterCartAdd() {
    selectedSession.value = null;
    selectedDate.value = undefined;
    hasCheckedOnce.value = false;
    availableSessions.value = [];
    calendarData.value = [];
    resetCalendarPagination();
    loadCalendarAvailabilities();
  }

  onScopeDispose(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (calendarAbortController) {
      calendarAbortController.abort();
    }
    if (availabilityAbortController) {
      availabilityAbortController.abort();
    }
  });

  return {
    currentLocale,
    isMobile,
    bookingType,
    selectedOption,
    selectedUnits,
    selectedDate,
    selectedSession,
    calendarData,
    availableSessions,
    isLoadingCalendar,
    isLoadingAvailabilities,
    isAddingToCart,
    bookingError,
    hasCheckedOnce,
    productTimezone,
    loadCalendarAvailabilities,
    searchAvailabilities,
    handleOptionSelected,
    handleUnitsSelected,
    handleDateSelected,
    handleBookingTypeSelected,
    handleSessionSelected,
    registerDateDropdownOpener,
    openDateDropdown,
    registerCalendarPaginationResetter,
    resetCalendarPagination,
    resetAfterCartAdd,
  };
}
